// src\components\common\ProductionProgress.jsx
import React from 'react';
import { Gauge, Clock, Calendar, TrendingUp } from 'lucide-react';

/**
 * Composant pour afficher la progression de la production
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.stats - Statistiques de production
 * @param {Date} props.currentDate - Date actuelle
 * @param {boolean} props.isDetailed - Afficher les détails supplémentaires
 */
const ProductionProgress = ({ stats, currentDate = new Date(), isDetailed = false }) => {
  // Calcul des pourcentages
  const completionRate = stats.dayScheduled > 0 
    ? Math.round((stats.dayCompleted / stats.dayScheduled) * 100) 
    : 0;
  
  const inProgressRate = stats.totalScheduled > 0 
    ? Math.round((stats.totalInProgress / stats.totalScheduled) * 100) 
    : 0;
  
  // Déterminer la couleur en fonction du taux de progression
  const getProgressColor = (rate) => {
    if (rate < 25) return 'text-red-600';
    if (rate < 50) return 'text-yellow-600';
    if (rate < 75) return 'text-blue-600';
    return 'text-green-600';
  };
  
  // Déterminer la couleur du background en fonction du taux de progression
  const getProgressBgColor = (rate) => {
    if (rate < 25) return 'bg-red-500';
    if (rate < 50) return 'bg-yellow-500';
    if (rate < 75) return 'bg-blue-500';
    return 'bg-green-500';
  };
  
  // Formater la date
  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-2 border-b flex justify-between items-center">
        <h2 className="text-md font-semibold text-gray-800 flex items-center">
          <Gauge size={24} className="mr-1 text-blue-600" />
          Progression de la journée
        </h2>
        <div className="text-xs text-gray-500 flex items-center">
          <Calendar size={20} className="mr-1" />
          {formatDate(currentDate)}
        </div>
      </div>
      
      <div className="p-3">
        {/* Jauge principale de progression */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1">
            <div className="font-medium text-sm">Global</div>
            <div className={`text-md font-bold ${getProgressColor(completionRate)}`}>
              {completionRate}%
            </div>
          </div>
          <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`absolute top-0 left-0 h-full ${getProgressBgColor(completionRate)}`}
              style={{ width: `${completionRate}%` }}
            ></div>
            <div 
              className="absolute top-0 left-0 h-full bg-blue-500 bg-opacity-50"
              style={{ width: `${Math.min(completionRate + inProgressRate, 100)}%` }}
            ></div>
          </div>
        </div>
        
        {/* Statistiques en version compacte */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-blue-50 p-2 rounded-lg">
            <div className="flex justify-between">
              <div className="text-xs text-gray-600">Prévues</div>
              <div className="text-md font-bold text-blue-700">{stats.dayScheduled}</div>
            </div>
            {isDetailed && (
              <div className="text-xs text-gray-500 mt-1">
                <div className="flex justify-between">
                  <span>Semaine:</span>
                  <span>{stats.weekScheduled}</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="bg-green-50 p-2 rounded-lg">
            <div className="flex justify-between">
              <div className="text-xs text-gray-600">Terminées</div>
              <div className="text-md font-bold text-green-700">{stats.dayCompleted}</div>
            </div>
            {isDetailed && (
              <div className="text-xs text-gray-500 mt-1">
                <div className="flex justify-between">
                  <span>Semaine:</span>
                  <span>{stats.weekCompleted}</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="bg-yellow-50 p-2 rounded-lg">
            <div className="flex justify-between">
              <div className="text-xs text-gray-600">En cours</div>
              <div className="text-md font-bold text-yellow-700">{stats.totalInProgress}</div>
            </div>
            {isDetailed && stats.estimatedCompletionTime && (
              <div className="text-xs text-gray-500 mt-1 flex items-center">
                <Clock size={10} className="mr-1 text-yellow-500" />
                <span>Fin ~{stats.estimatedCompletionTime}</span>
              </div>
            )}
          </div>
          
          <div className="bg-red-50 p-2 rounded-lg">
            <div className="flex justify-between">
              <div className="text-xs text-gray-600">Alertes</div>
              <div className="text-md font-bold text-red-700">{stats.totalAlerts}</div>
            </div>
            {isDetailed && stats.totalAlerts > 0 && (
              <div className="text-xs text-red-500 mt-1">
                {stats.totalAlerts} {stats.totalAlerts > 1 ? 'problèmes' : 'problème'}
              </div>
            )}
          </div>
        </div>
        
        {/* Semaine vs semaine précédente - version compacte */}
        {isDetailed && stats.trend !== undefined && (
          <div className="mt-3 bg-gray-50 p-2 rounded-lg">
            <div className="flex justify-between items-center">
              <div className="text-xs text-gray-600 flex items-center">
                <TrendingUp size={12} className="mr-1 text-blue-600" />
                Tendance hebdo
              </div>
              <div className={`text-xs font-medium ${
                stats.trend > 0 ? 'text-green-600' : stats.trend < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                {stats.trend > 0 ? '+' : ''}{stats.trend}% vs sem. préc.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionProgress;